/**
 * useNativeCommunityVideos
 *
 * The community analogue of useNativeRitualVideos: drives native
 * (Cloudflare-hosted) community video for the shared MediaUploadSection grid so
 * video sits inline alongside images, exactly like rituals. Lists existing
 * videos, uploads through the GCS -> branded-intro -> Cloudflare Stream flow,
 * deletes, and tracks status.
 *
 * Two differences from the ritual hook:
 *  - No realtime: community native video doesn't emit Ably events yet, so we
 *    POLL the list endpoint while any row is still pending/processing.
 *  - Deferred create: native upload needs an existing communityId, which the
 *    CREATE flow lacks. uploadVideo queues the file (shown as a card) when
 *    there's no communityId; the create page calls flushPending(newCommunityId)
 *    after the community is created.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
// Reuse the ritual GCS resumable-upload helpers + client-side gates (all
// owner-agnostic).
import {
  isAbortError,
  probeVideoDuration,
  uploadResumable,
} from "@/features/rituals/utils/gcsResumableUpload"
import { validateAndProbeVideo } from "@/features/rituals/utils/videoGates"
import { looksLikeVideoFile } from "@/utils/fileTypes"
import {
  type CommunityVideoStatus,
  confirmCommunityVideoUpload,
  deleteCommunityVideo,
  listCommunityVideos,
  requestCommunityVideoUploadUrl,
} from "../services/communityVideo.service"

const POLL_INTERVAL_MS = 4000

export type NativeVideoStatus = CommunityVideoStatus | "uploading" | "error"

export interface NativeVideoItem {
  /** Stable local id (also used while pending a server id). */
  localId: string
  /** Server-issued community_videos id once the row exists. */
  communityVideoId: string | null
  status: NativeVideoStatus
  filename: string
  /** User-specified display title. Defaults to filename on upload. */
  title: string
  thumbnailUrl: string | null
  cloudflareUid: string | null
  sortOrder: number
  uploadedBytes: number
  totalBytes: number
  errorMessage: string | null
}

export interface UseNativeCommunityVideosResult {
  videos: NativeVideoItem[]
  isVideoFile: (file: File) => boolean
  uploadVideo: (file: File) => Promise<void>
  deleteVideo: (localId: string) => Promise<void>
  flushPending: (newCommunityId: string) => Promise<void>
  hasPendingUploads: boolean
  /** Whether any video is actively uploading -- the page uses this to guard
   *  against navigating away mid-upload. (Community has no "queued" state;
   *  deferred creates stay "uploading".) */
  hasActiveUpload: boolean
}

export function useNativeCommunityVideos(communityId: string): UseNativeCommunityVideosResult {
  const [videos, setVideos] = useState<NativeVideoItem[]>([])
  // Mirror of `videos` so callbacks can read the latest list without taking it
  // as a dependency (keeps their identity stable for memoized consumers).
  const videosRef = useRef(videos)
  videosRef.current = videos
  const hasLoaded = useRef(false)
  const pendingFilesRef = useRef<Map<string, File>>(new Map())
  const flushingRef = useRef(false)
  const [hasPendingUploads, setHasPendingUploads] = useState(false)
  // Per-upload abort controllers keyed by localId, so concurrent uploads (the
  // picker allows multi-select) each get their own and can be cancelled
  // independently. A 2GB upload would otherwise keep running detached on
  // unmount, burning uplink + patching a dead component.
  const uploadAbortsRef = useRef<Map<string, AbortController>>(new Map())
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      for (const controller of uploadAbortsRef.current.values()) controller.abort()
      uploadAbortsRef.current.clear()
    }
  }, [])

  const patch = useCallback((localId: string, next: Partial<NativeVideoItem>) => {
    if (!mountedRef.current) return // don't setState after unmount
    setVideos(prev => prev.map(v => (v.localId === localId ? { ...v, ...next } : v)))
  }, [])

  // Progress-only update that bails when the byte count is unchanged, so the
  // 250ms synthetic-progress ticks don't force a full media-grid re-render on
  // every tick.
  const patchProgress = useCallback((localId: string, uploaded: number) => {
    if (!mountedRef.current) return
    setVideos(prev => {
      const idx = prev.findIndex(v => v.localId === localId)
      if (idx === -1 || prev[idx].uploadedBytes === uploaded) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], uploadedBytes: uploaded }
      return next
    })
  }, [])

  // Seed existing videos on mount so managers can see + remove them.
  useEffect(() => {
    if (!communityId || hasLoaded.current) return
    hasLoaded.current = true
    listCommunityVideos(communityId)
      .then(list => {
        const existing: NativeVideoItem[] = list.map(v => ({
          localId: `existing-${v.id}`,
          communityVideoId: v.id,
          status: v.status,
          filename: v.title || "Video",
          title: v.title || "Video",
          thumbnailUrl: v.thumbnailUrl,
          cloudflareUid: v.cloudflareUid,
          sortOrder: v.sortOrder,
          uploadedBytes: 1,
          totalBytes: 1,
          errorMessage: v.failureReason,
        }))
        setVideos(prev => {
          const seen = new Set(
            prev.map(v => v.communityVideoId).filter((id): id is string => id !== null),
          )
          // existing rows always carry a server id; the null guard is for the type.
          return [
            ...existing.filter(e => e.communityVideoId !== null && !seen.has(e.communityVideoId)),
            ...prev,
          ]
        })
      })
      .catch(() => {
        // Reset so a later render retries; surface it so a failed load isn't
        // mistaken for "no videos" (which could prompt a duplicate re-upload).
        hasLoaded.current = false
        if (mountedRef.current) toast.error("Couldn't load existing videos. Refresh to retry.")
      })
  }, [communityId])

  // Poll while any server row is still pending/processing (no realtime yet).
  // Guards: a leading fetch (no 4s dead stare), an in-flight flag (no overlap /
  // out-of-order stomping on slow connections), and a cancelled flag (drop a
  // late response after the effect re-runs or the component unmounts).
  const anyProcessing = videos.some(v => v.status === "pending" || v.status === "processing")
  useEffect(() => {
    if (!communityId || !anyProcessing) return
    let cancelled = false
    let inFlight = false
    let polls = 0
    const MAX_POLLS = 60 // ceiling (~4 min at 4s) so a stuck row doesn't poll forever
    const tick = async () => {
      if (inFlight) return
      if (polls >= MAX_POLLS) {
        clearInterval(timer)
        return
      }
      polls += 1
      inFlight = true
      try {
        const list = await listCommunityVideos(communityId)
        if (cancelled) return // late response: effect re-ran or unmounted
        const byId = new Map(list.map(v => [v.id, v]))
        setVideos(prev =>
          prev.map(v => {
            if (!v.communityVideoId) return v
            const server = byId.get(v.communityVideoId)
            if (!server) return v
            return {
              ...v,
              status: server.status,
              cloudflareUid: server.cloudflareUid,
              thumbnailUrl: server.thumbnailUrl ?? v.thumbnailUrl,
              errorMessage: server.failureReason ?? v.errorMessage,
            }
          }),
        )
      } catch {
        // transient; the next tick recovers
      } finally {
        inFlight = false
      }
    }
    const timer = setInterval(tick, POLL_INTERVAL_MS)
    void tick() // leading edge (no 4s dead stare)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [communityId, anyProcessing])

  // Extension fallback so an Android .mp4 with an empty/generic MIME still
  // routes to the video pipeline instead of the image path.
  const isVideoFile = useCallback((file: File) => looksLikeVideoFile(file), [])

  const performUpload = useCallback(
    async (localId: string, file: File, targetCommunityId: string, duration: number) => {
      patch(localId, { status: "uploading", uploadedBytes: 0, errorMessage: null })
      const controller = new AbortController()
      uploadAbortsRef.current.set(localId, controller)
      try {
        const signed = await requestCommunityVideoUploadUrl(targetCommunityId, {
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          durationSeconds: duration > 0 ? Math.ceil(duration) : 0,
        })
        await uploadResumable({
          signedUrl: signed.signedUrl,
          file,
          contentType: file.type,
          signal: controller.signal,
          onProgress: uploaded => patchProgress(localId, uploaded),
        })
        const confirmed = await confirmCommunityVideoUpload(targetCommunityId, {
          gcsPath: signed.gcsPath,
          title: file.name,
        })
        patch(localId, { communityVideoId: confirmed.communityVideoId, status: confirmed.status })
      } catch (error) {
        // An abort is an intentional cancel (navigation/unmount or a sibling
        // upload). Don't surface a "signal is aborted" toast. If still mounted,
        // move the card off "uploading" so the nav guard releases.
        if (isAbortError(error)) {
          if (mountedRef.current)
            patch(localId, { status: "error", errorMessage: "Upload cancelled" })
          throw error
        }
        const message = error instanceof Error ? error.message : "Upload failed. Please try again."
        patch(localId, { status: "error", errorMessage: message })
        throw error instanceof Error ? error : new Error(message)
      } finally {
        uploadAbortsRef.current.delete(localId)
      }
    },
    [patch, patchProgress],
  )

  const uploadVideo = useCallback(
    async (file: File) => {
      let duration: number
      try {
        duration = await validateAndProbeVideo(file)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Invalid video")
        return
      }

      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      setVideos(prev => [
        ...prev,
        {
          localId,
          communityVideoId: null,
          status: "uploading",
          filename: file.name,
          title: file.name,
          thumbnailUrl: null,
          cloudflareUid: null,
          sortOrder: prev.length,
          uploadedBytes: 0,
          totalBytes: file.size,
          errorMessage: null,
        },
      ])

      // Create flow: no communityId yet -> queue; flushPending uploads it once
      // the community exists.
      if (!communityId) {
        pendingFilesRef.current.set(localId, file)
        setHasPendingUploads(true)
        return
      }

      try {
        await performUpload(localId, file, communityId, duration)
      } catch (error) {
        // Swallow intentional cancels -- a navigated-away upload must not pop a
        // "signal is aborted without reason" error toast.
        if (isAbortError(error)) return
        toast.error(error instanceof Error ? error.message : "Upload failed. Please try again.")
      }
    },
    [communityId, performUpload],
  )

  const flushPending = useCallback(
    async (newCommunityId: string) => {
      // Re-entrancy guard: a double-submit must not start two flushes that both
      // snapshot + re-upload the same queued files.
      if (flushingRef.current) return
      flushingRef.current = true
      let failed = 0
      try {
        for (;;) {
          if (!mountedRef.current) break
          const next = pendingFilesRef.current.entries().next()
          if (next.done) break
          const [localId, file] = next.value
          // Claim the entry (delete BEFORE the long upload) so a concurrent
          // flush can't re-grab the same file mid-upload.
          pendingFilesRef.current.delete(localId)
          let duration = 0
          try {
            duration = await probeVideoDuration(file)
          } catch {
            duration = 0
          }
          try {
            await performUpload(localId, file, newCommunityId, duration)
          } catch (error) {
            // Abort (navigated away mid-flush) stops the whole queue quietly.
            if (isAbortError(error)) break
            // A real failure: card is already marked errored; count it and keep
            // going so one bad file doesn't block the rest.
            failed++
          }
        }
        setHasPendingUploads(false)
      } finally {
        flushingRef.current = false
      }
      // Re-throw so the create page surfaces "saved, but a video failed to
      // upload" -- otherwise the failure is invisible after navigation.
      if (failed > 0) {
        throw new Error(`${failed} video${failed > 1 ? "s" : ""} failed to upload`)
      }
    },
    [performUpload],
  )

  const deleteVideo = useCallback(
    async (localId: string) => {
      const item = videosRef.current.find(v => v.localId === localId)
      if (!item) return
      if (pendingFilesRef.current.has(localId)) {
        pendingFilesRef.current.delete(localId)
        setHasPendingUploads(pendingFilesRef.current.size > 0)
        setVideos(prev => prev.filter(v => v.localId !== localId))
        return
      }
      if (item.communityVideoId) {
        try {
          await deleteCommunityVideo(communityId, item.communityVideoId)
        } catch (error) {
          toast.error(error instanceof Error ? error.message : "Failed to delete video")
          return
        }
      }
      setVideos(prev => prev.filter(v => v.localId !== localId))
    },
    [communityId],
  )

  const hasActiveUpload = videos.some(v => v.status === "uploading")

  return {
    videos,
    isVideoFile,
    uploadVideo,
    deleteVideo,
    flushPending,
    hasPendingUploads,
    hasActiveUpload,
  }
}
